// 치과 「치과 안내 보내기」(알림 파트 B) — Cloud Functions 호출 모음 (2026-10-08)
// 기획서 docs/기획서_환자자동관리_알림파트_2026-10-07.md §4-2·§5 (대표 10-08 Q1~Q9 권고대로 승인).
// 서버 계약: docs/계약_알림파트_서버_2026-10-08.md §B (대조 완료 2026-10-08). 응답 필드는 없으면 안전한 빈 상태.
//   계약이 바뀌면 이 파일 한 곳만 고친다(화면은 CF 이름·형태를 직접 알지 않는다).
// 원칙: 고정 템플릿 5종 + 변수(날짜·시간·선택지)만 — 자유 문구 입력 없음. 환자가 보는 본문은 서버가 만든다(웹은 미리보기 응답을 그대로 표시).
//   즉시 발송·재발송·내보내기 없음. 발송 시각 = 서버 고정 10:20 KST. 안내 발송·취소 = 치과 대표만(서버가 최종 판정).
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

export const NOTIFY_GENERIC_ERROR = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

// 템플릿 카탈로그(치과 화면 라벨) — 기획 §4-2 B-1. 코드는 서버 NOTICE_TEMPLATES 와 같아야 한다
export const NOTICE_TEMPLATES = Object.freeze([
    { code: 'notice_closed', label: '휴진 안내', desc: '휴진 기간과 진료 재개일을 알립니다.' },
    { code: 'notice_hours', label: '진료시간 변경', desc: '바뀌는 날짜와 새 진료시간을 알립니다.' },
    { code: 'notice_visit', label: '예약 확인', desc: '환자 1명에게 방문 예정 날짜·시각을 알립니다.' },
    { code: 'notice_prepare', label: '방문 시 준비물', desc: '방문할 때 가져올 것을 고정 항목에서 골라 알립니다.' },
    { code: 'notice_contact', label: '연락처·이전 안내', desc: '대표전화 변경 또는 치과 이전을 알립니다(등록된 정보만 사용).' },
]);
export const NOTICE_TEMPLATE_CODES = Object.freeze(NOTICE_TEMPLATES.map(t => t.code));
export const noticeTemplateLabel = (code) => (NOTICE_TEMPLATES.find(t => t.code === code) || {}).label || '치과 안내';

// 방문 준비물 고정 선택지(≤3) — 계약 B-0 키·표시(§E-3: '복용'은 금지어라 "현재 드시는 약 목록"). listClinicNotices catalog.prepareOptions 가 오면 그것을 우선
export const PREPARE_ITEMS = Object.freeze([
    { code: 'id_card', label: '신분증' },
    { code: 'health_card', label: '건강보험증(또는 모바일 건강보험증)' },
    { code: 'med_list', label: '현재 드시는 약 목록' },
    { code: 'referral', label: '다른 병원 처방전·의뢰서' },
    { code: 'prior_records', label: '이전 치료 기록·엑스레이 CD' },
    { code: 'guardian_consent', label: '보호자 동의서' },
]);
// 응답 catalog → 준비물 선택지 [{code,label}] (없거나 형식 오류면 폴백)
export function prepareOptionsOf(catalog) {
    const row = Array.isArray(catalog) ? catalog.find(c => c && c.code === 'notice_prepare') : null;
    const opts = Array.isArray(row?.prepareOptions) ? row.prepareOptions.filter(o => o && typeof o.key === 'string' && typeof o.label === 'string') : [];
    return opts.length ? opts.map(o => ({ code: o.key, label: o.label })) : PREPARE_ITEMS;
}
export const PREPARE_MAX = 3;

// 대상 — (a) 등록 환자 전체 (b) 최근 N개월 방문(기본 12개월 — 대표 결정 Q3) (c) 개별 선택(≤300). 예약 확인은 (c) 1명만
export const AUDIENCE_MODES = Object.freeze([
    { mode: 'recent', label: '최근 환자' },   // 계약 §E-5 기준 = max(의뢰서 연결일·확인일·등록일)
    { mode: 'all', label: '등록 환자 전체' },
    { mode: 'pick', label: '직접 고르기' },
]);
export const RECENT_MONTH_OPTIONS = Object.freeze([3, 6, 12]);
export const DEFAULT_RECENT_MONTHS = 12;
export const PICK_MAX = 300;
export const SEND_MAX_AHEAD_DAYS = 30;     // 발송일 = 오늘 ~ 30일 뒤
export const VAR_MAX_AHEAD_DAYS = 90;      // 휴진 시작·변경일·방문일·이전일 = 오늘 ~ 90일 뒤(계약 B-0)
export const CLOSED_MAX_SPAN_DAYS = 31;    // 휴진 마지막 날 ≤ 시작일 + 31
export const REOPEN_MAX_AFTER_DAYS = 14;   // 재개일 ≤ 마지막 날 + 14
export const TODAY_CUTOFF_MIN = 10 * 60 + 15;   // 오늘 발송은 10:15 KST 전 예약분만(계약 §E-10)
export const SEND_TIME_TEXT = '오전 10시 20분';   // 서버 고정 발송 시각(치과가 바꿀 수 없음)
// 상한(대표 결정 Q6) — 화면 안내용. 실제 판정은 서버(거부 시 서버 사유를 그대로 표시)
export const LIMITS_TEXT = '치과당 한 달 4건(예약 확인 제외) · 같은 종류는 7일에 1건 · 1건당 최대 1,000명';

export const NOTICE_STATUS_LABEL = Object.freeze({ scheduled: '예약됨', sending: '보내는 중', sent: '보냄', cancelled: '취소됨', expired: '만료' });
export const noticeStatusLabel = (s) => NOTICE_STATUS_LABEL[s] || s || '-';
// 미전달·건너뜀 사유(서버 결과 코드) → 화면 문구. 모르는 코드는 코드 그대로(숨기지 않음)
export const NOTICE_SKIP_LABEL = Object.freeze({
    // 미리보기 counts.skipped(계약 B-1)
    notify_off: '알림 꺼짐', not_recent: '최근 방문 기간 밖', inactive: '등록 상태 아님', not_found: '환자 정보 없음',
    // 발송 결과 counts.skipped = pushLogs result(계약 B-4) — 안내에서 no_consent 는 "알림 수신 꺼짐"
    no_consent: '알림 꺼짐', no_token: '앱 없음', no_verified_device: '확인된 기기 없음', failed: '발송 실패', gate_closed: '환자 앱 위탁 확인 닫힘',
    not_allowed: '이 환자에게는 보낼 수 없음', not_delivered: '기간 안에 전달하지 못함', error: '오류',
});
export const noticeSkipLabel = (code) => NOTICE_SKIP_LABEL[code] || code || '사유 미상';

// ── 날짜·시간 보조(순수) ──
export const pad2 = (n) => String(n).padStart(2, '0');
export const localDayKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
// 서버 기준 날짜는 KST — 브라우저 시간대와 무관하게 KST 오늘·분(0~1439)을 계산
const KST_MS = 9 * 60 * 60 * 1000;
export function kstNow(ms = Date.now()) {
    const d = new Date(ms + KST_MS);
    return { dayKey: d.toISOString().slice(0, 10), minutes: d.getUTCHours() * 60 + d.getUTCMinutes() };
}
// 고를 수 있는 가장 이른 발송일 — 10:15 KST 이후면 내일
export function earliestSendDay(ms = Date.now()) {
    const k = kstNow(ms);
    return k.minutes < TODAY_CUTOFF_MIN ? k.dayKey : addDaysKey(k.dayKey, 1);
}
export const addDaysKey = (key, n) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
    if (!m) return '';
    return localDayKey(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
};
// 30분 단위 시각 선택지(07:00~22:00) — 자유 입력 없이 셀렉트만
export const TIME_OPTIONS = Object.freeze((() => {
    const out = [];
    for (let h = 7; h <= 22; h += 1) { out.push(`${pad2(h)}:00`); if (h < 22) out.push(`${pad2(h)}:30`); }
    return out;
})());
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const HM = /^\d{2}:\d{2}$/;

// 변수 1차 검증(순수, 계약 B-0 규칙) — 서버가 최종 판정. todayKey = KST 오늘. 반환 '' = 통과, 아니면 화면 문구
export function validateNoticeVarsLocal(code, v, todayKey) {
    const x = v || {};
    const maxDay = todayKey ? addDaysKey(todayKey, VAR_MAX_AHEAD_DAYS) : '';
    const inRange = (k) => !todayKey || (k >= todayKey && k <= maxDay);
    if (code === 'notice_closed') {
        if (!YMD.test(x.from || '') || !YMD.test(x.to || '')) return '휴진 시작일과 마지막 날을 고르세요.';
        if (!inRange(x.from)) return `휴진 시작일은 오늘부터 ${VAR_MAX_AHEAD_DAYS}일 안에서 고르세요.`;
        if (x.to < x.from) return '휴진 마지막 날은 시작일보다 같거나 뒤여야 합니다.';
        if (x.to > addDaysKey(x.from, CLOSED_MAX_SPAN_DAYS)) return `휴진 기간은 시작일부터 ${CLOSED_MAX_SPAN_DAYS}일 안이어야 합니다.`;
        if (x.reopen && (!YMD.test(x.reopen) || x.reopen <= x.to || x.reopen > addDaysKey(x.to, REOPEN_MAX_AFTER_DAYS))) return `진료 재개일은 휴진 마지막 날 다음 날부터 ${REOPEN_MAX_AFTER_DAYS}일 안이어야 합니다.`;
        return '';
    }
    if (code === 'notice_hours') {
        if (!YMD.test(x.from || '')) return '바뀌는 날짜를 고르세요.';
        if (!inRange(x.from)) return `바뀌는 날짜는 오늘부터 ${VAR_MAX_AHEAD_DAYS}일 안에서 고르세요.`;
        if (!HM.test(x.open || '') || !HM.test(x.close || '')) return '진료 시작·종료 시각을 고르세요.';
        if (x.open >= x.close) return '진료 종료 시각은 시작 시각보다 뒤여야 합니다.';
        if (x.lunchStart || x.lunchEnd) {
            if (!HM.test(x.lunchStart || '') || !HM.test(x.lunchEnd || '')) return '점심시간은 시작·종료를 모두 고르거나 둘 다 비워 두세요.';
            if (x.lunchStart >= x.lunchEnd) return '점심시간 종료는 시작보다 뒤여야 합니다.';
            if (x.lunchStart < x.open || x.lunchEnd > x.close) return '점심시간은 진료시간 안에 있어야 합니다.';
        }
        return '';
    }
    if (code === 'notice_visit') {
        if (!YMD.test(x.visitDate || '')) return '방문 날짜를 고르세요.';
        if (!inRange(x.visitDate)) return `방문 날짜는 오늘부터 ${VAR_MAX_AHEAD_DAYS}일 안에서 고르세요.`;
        if (x.visitTime && !HM.test(x.visitTime)) return '방문 시각을 다시 고르세요.';
        return '';
    }
    if (code === 'notice_prepare') {
        const items = Array.isArray(x.prepare) ? x.prepare : [];
        if (items.length < 1 || items.length > PREPARE_MAX) return `준비물을 1~${PREPARE_MAX}개 고르세요.`;
        return '';
    }
    if (code === 'notice_contact') {
        if (x.kind !== 'phone' && x.kind !== 'move') return '대표전화 변경 또는 이전 안내 중 하나를 고르세요.';
        if (x.kind === 'move' && !YMD.test(x.from || '')) return '이전 날짜를 고르세요.';
        if (x.kind === 'move' && !inRange(x.from)) return `이전 날짜는 오늘부터 ${VAR_MAX_AHEAD_DAYS}일 안에서 고르세요.`;
        return '';
    }
    return '안내 종류를 고르세요.';
}

// 발송일 검증(계약 B-1) — earliest = earliestSendDay(), closed: sendDay ≤ to, visit: sendDay ≤ visitDate
export function validateSendDayLocal(code, vars, sendDay, earliest) {
    if (!YMD.test(sendDay || '')) return '발송일을 고르세요.';
    const max = addDaysKey(earliest, SEND_MAX_AHEAD_DAYS);
    if (sendDay < earliest || sendDay > max) return `발송일은 ${earliest.replace(/-/g, '.')}부터 ${SEND_MAX_AHEAD_DAYS}일 안에서 고르세요(오늘 발송은 오전 10시 15분 전 예약만).`;
    if (code === 'notice_closed' && vars?.to && sendDay > vars.to) return '휴진 안내 발송일은 휴진 마지막 날보다 늦을 수 없습니다.';
    if (code === 'notice_visit' && vars?.visitDate && sendDay > vars.visitDate) return '예약 확인 발송일은 방문 날짜보다 늦을 수 없습니다.';
    return '';
}

// 서버로 보낼 vars — 템플릿별 허용 키만(빈 값 제거). 전화·주소는 서버가 스냅샷(치과 입력 없음)
export function buildNoticeVars(code, v) {
    const x = v || {};
    const pick = (keys) => keys.reduce((o, k) => { if (x[k] != null && x[k] !== '') o[k] = x[k]; return o; }, {});
    if (code === 'notice_closed') return pick(['from', 'to', 'reopen']);
    if (code === 'notice_hours') {
        const o = pick(['from', 'open', 'close']);
        if (x.lunchStart && x.lunchEnd) { o.lunchStart = x.lunchStart; o.lunchEnd = x.lunchEnd; }
        return o;
    }
    if (code === 'notice_visit') return pick(['visitDate', 'visitTime']);
    if (code === 'notice_prepare') return { prepare: (Array.isArray(x.prepare) ? x.prepare : []).slice(0, PREPARE_MAX) };
    if (code === 'notice_contact') return x.kind === 'move' ? { kind: 'move', from: x.from } : { kind: 'phone' };
    return {};
}

// 대상 payload — pick 은 pids 만(이름 등 식별정보는 보내지 않는다)
export function buildAudience({ mode, months, pids }) {
    if (mode === 'pick') return { mode: 'pick', pids: (Array.isArray(pids) ? pids : []).slice(0, PICK_MAX) };
    if (mode === 'recent') return { mode: 'recent', months: RECENT_MONTH_OPTIONS.includes(months) ? months : DEFAULT_RECENT_MONTHS };
    return { mode: 'all' };
}

// counts.skipped 등 { reason: n } → [{reason,label,n}] (순수)
export function skipEntries(obj) {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj).filter(([, n]) => Number.isFinite(n) && n > 0).map(([reason, n]) => ({ reason, label: noticeSkipLabel(reason), n }));
}

const num = (v) => (Number.isFinite(v) ? v : null);

// 계약 B-1 previewClinicNotice — 대표만. 입력 { templateCode, vars, audience, sendDay }
//   응답 { ok, templateCode, label, title, body, lockScreen, sendDay, sendTime, counts{scanned,targets,eligible,skipped{}}, noApp, caps{monthUsed,monthMax,cooldownDays,cooldownBlocked,perNoticeMax}, warnings[] }
//   pids 없음(건수만). 상한 초과는 오류가 아니라 caps 로 온다 → capBlocks 로 예약 버튼 비활성 사유를 만든다
export async function previewClinicNotice({ templateCode, vars, audience, sendDay }) {
    const res = await call('previewClinicNotice')({ templateCode, vars, audience, sendDay });
    const d = res.data || {};
    const c = d.counts || {};
    const caps = d.caps || {};
    const eligible = num(c.eligible);
    const capBlocks = [];
    const isVisit = templateCode === 'notice_visit';
    if (!isVisit && Number.isFinite(caps.monthUsed) && Number.isFinite(caps.monthMax) && caps.monthUsed >= caps.monthMax) capBlocks.push(`이번 달 안내 상한(${caps.monthMax}건)을 이미 채웠습니다.`);
    if (!isVisit && caps.cooldownBlocked === true) capBlocks.push(`같은 종류의 안내가 발송일 앞뒤 ${Number.isFinite(caps.cooldownDays) ? caps.cooldownDays : 7}일 안에 이미 있습니다.`);
    if (Number.isFinite(eligible) && Number.isFinite(caps.perNoticeMax) && eligible > caps.perNoticeMax) capBlocks.push(`받을 수 있는 환자가 ${eligible}명으로 1건 상한(${caps.perNoticeMax}명)을 넘습니다. 대상을 줄여 주세요.`);
    if (eligible === 0) capBlocks.push('받을 수 있는 환자가 없습니다.');
    return {
        ok: d.ok === true,
        label: typeof d.label === 'string' ? d.label : noticeTemplateLabel(templateCode),
        text: typeof d.body === 'string' ? d.body : '',
        title: typeof d.title === 'string' ? d.title : '',
        lockText: typeof d.lockScreen === 'string' ? d.lockScreen : '',
        sendDay: typeof d.sendDay === 'string' ? d.sendDay : sendDay,
        sendTime: typeof d.sendTime === 'string' ? d.sendTime : '',
        targets: num(c.targets),
        sendable: eligible,
        noApp: num(d.noApp),
        skipped: skipEntries(c.skipped),
        caps,
        capBlocks,
        closedShortLead: Array.isArray(d.warnings) && d.warnings.includes('closed_short_lead'),
        raw: d,
    };
}

// 계약 B-2 scheduleClinicNotice — 대표만. 게이트·상한 검사 후 scheduled 문서 생성. 응답 { ok, noticeId, sendDay, sendTime, counts{targets} }
//   거부 details.code: notify-delegation-required · no-sender · clinic-name-banned · monthly-cap · template-cooldown · visit-duplicate · audience-too-large · no-target · no-contact · no-address · invalid-send-day · invalid-audience
export async function scheduleClinicNotice({ templateCode, vars, audience, sendDay }) {
    const res = await call('scheduleClinicNotice')({ templateCode, vars, audience, sendDay });
    const d = res.data || {};
    return { ok: d.ok === true, noticeId: typeof d.noticeId === 'string' ? d.noticeId : '', sendDay: typeof d.sendDay === 'string' ? d.sendDay : sendDay, targets: num(d.counts?.targets), raw: d };
}

// 계약 B-3 cancelClinicNotice — 대표만. scheduled·미발송만 → cancelled. 응답 { ok, already, status } · 불가 failed-precondition {code:'notice-not-cancellable'}
export async function cancelClinicNotice(noticeId) {
    const res = await call('cancelClinicNotice')({ noticeId });
    return res.data || { ok: false };
}

// 계약 B-4 listClinicNotices — 오너·직원. sendDay 최근 90일 ~ 앞으로 30일, 최신순 ≤100.
//   응답 { ok, role, canSend, gate{flag,delegationOk,delegationVersion,versions,clinicNameMissing,clinicNameBanned,clinicPhoneMissing}, catalog[], caps{monthUsed,monthMax}, items[], truncated }
export async function listClinicNotices() {
    const res = await call('listClinicNotices')({});
    const d = res.data || {};
    const items = (Array.isArray(d.items) ? d.items : []).filter(r => r && typeof r.noticeId === 'string').map(r => {
        const c = r.counts || {};
        const a = r.audience || {};
        return {
            noticeId: r.noticeId,
            templateCode: r.templateCode || '',
            label: typeof r.label === 'string' && r.label ? r.label : noticeTemplateLabel(r.templateCode),
            body: typeof r.body === 'string' ? r.body : '',
            sendDay: typeof r.sendDay === 'string' ? r.sendDay : '',
            status: r.status || '',
            statusLabel: noticeStatusLabel(r.status),
            targets: num(c.targets),
            sent: num(c.sent),
            pending: num(c.pending),
            skipped: skipEntries(c.skipped),
            audienceText: a.mode === 'recent' ? `최근 ${a.months || DEFAULT_RECENT_MONTHS}개월 등록·의뢰` : a.mode === 'all' ? '등록 환자 전체' : a.mode === 'pick' ? '직접 고름' : '',
            deferredDays: num(r.deferredDays),
            expiredReason: r.expiredReason || '',
            canCancel: r.canCancel === true,
        };
    });
    const g = d.gate || {};
    const caps = d.caps || {};
    return {
        ok: d.ok !== false,
        items,
        truncated: d.truncated === true,
        role: d.role || null,
        canSend: typeof d.canSend === 'boolean' ? d.canSend : null,                 // null = 응답에 없음 → 화면은 오너 여부로 판정
        delegationOk: typeof g.delegationOk === 'boolean' ? g.delegationOk : null,   // false = 환자 앱 처리위탁 확인 없음 → B 블록 비활성
        blockers: [g.clinicNameMissing === true ? 'name' : null, g.clinicNameBanned === true ? 'banned' : null].filter(Boolean),   // 안내 발송 차단(no-sender·clinic-name-banned)
        clinicPhoneMissing: g.clinicPhoneMissing === true,
        prepareOptions: prepareOptionsOf(d.catalog),
        monthUsed: num(caps.monthUsed),
        monthLimit: num(caps.monthMax),
        raw: d,
    };
}

// CF 오류 → 화면 문구. 서버가 거부한 사유(상한·권한·위탁 확인 등)는 서버 메시지를 그대로 보여준다(대표 결정 Q6). 원본은 console.error
export function describeNotifyError(e, fallback = NOTIFY_GENERIC_ERROR) {
    console.error('[clinicAlertsApi]', e?.code || '', e?.message || e, e?.details || '');
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;
    const c = e?.details?.code;
    if (e?.code === 'functions/unauthenticated') return '로그인이 필요합니다.';
    if (e?.code === 'functions/permission-denied') return c === 'owner-only' ? '치과 대표만 할 수 있습니다.' : (e?.message || '권한이 없습니다.');
    if (e?.code === 'functions/resource-exhausted') return e?.message || '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/failed-precondition') {
        if (c === 'feature-off') return '치과 안내 기능이 아직 열리지 않았습니다.';
        if (c === 'notify-delegation-required') return '치과 대표의 환자 앱 처리위탁 확인이 있어야 안내를 보낼 수 있습니다. 환자 자동관리 화면의 처리위탁 확인 카드에서 확인해 주세요.';
        if (c === 'notice-not-cancellable') return '이미 보내기 시작했거나 끝난 안내라 취소할 수 없습니다.';
        return e?.message || fallback;   // monthly-cap·template-cooldown 등 — 서버 사유 그대로(대표 결정 Q6)
    }
    if (e?.code === 'functions/invalid-argument') return e?.message || '입력값을 확인해 주세요.';
    if (e?.code === 'functions/not-found') return e?.message || '안내를 찾을 수 없습니다. 목록을 새로고침해 주세요.';
    if (e?.code === 'functions/already-exists') return e?.message || '이미 처리된 요청입니다.';
    return fallback;
}
